import {
  resendOtp as apiResendOtp,
  sendOtp as apiSendOtp,
  verifyOtp as apiVerifyOtp,
  type UserResponse,
} from '@/services/authApi';
import { useAuth } from '@/contexts/AuthContext';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const RESEND_INTERVAL_SECONDS = 60;

export type LoginOtpStep = 'email' | 'otp';

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    return (
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      fallback
    );
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

export function useLoginOtp() {
  const { establishSession } = useAuth();
  const [step, setStep] = useState<LoginOtpStep>('email');
  const [userId, setUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef({ userId: '', uniqueCode: '' });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startResendTimer = useCallback(
    (seconds: number = RESEND_INTERVAL_SECONDS) => {
      clearTimer();
      setResendSecondsLeft(seconds);
      timerRef.current = setInterval(() => {
        setResendSecondsLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimer]
  );

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const sendOtp = async (email: string): Promise<boolean> => {
    setSending(true);
    setError(null);
    setMessage(null);

    const code = uuidv4();

    try {
      const response = await apiSendOtp({
        userId: email,
        uniqueCode: code,
      });
      sessionRef.current = { userId: email, uniqueCode: code };
      setUserId(email);
      setMessage(response.message);
      setStep('otp');
      startResendTimer();
      return true;
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send OTP'));
      return false;
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async (): Promise<boolean> => {
    const { userId: sessionUserId, uniqueCode: sessionUniqueCode } =
      sessionRef.current;
    if (!sessionUserId || !sessionUniqueCode || resendSecondsLeft > 0) {
      return false;
    }

    setSending(true);
    setError(null);
    setMessage(null);

    try {
      const response = await apiResendOtp({
        userId: sessionUserId,
        uniqueCode: sessionUniqueCode,
      });
      setMessage(response.message);
      startResendTimer();
      return true;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 429) {
        setError(getErrorMessage(err, 'Please wait before resending'));
        startResendTimer();
        return false;
      }
      setError(getErrorMessage(err, 'Failed to resend OTP'));
      return false;
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<UserResponse | null> => {
    const { userId: sessionUserId, uniqueCode: sessionUniqueCode } =
      sessionRef.current;
    if (!sessionUserId || !sessionUniqueCode) {
      return null;
    }

    setVerifying(true);
    setError(null);

    try {
      const user = await apiVerifyOtp({
        userId: sessionUserId,
        uniqueCode: sessionUniqueCode,
        otp,
      });
      establishSession(user);
      return user;
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid or expired OTP'));
      return null;
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    clearTimer();
    sessionRef.current = { userId: '', uniqueCode: '' };
    setStep('email');
    setUserId('');
    setError(null);
    setMessage(null);
    setResendSecondsLeft(0);
  };

  return {
    step,
    userId,
    sending,
    verifying,
    loading: sending || verifying,
    error,
    message,
    resendSecondsLeft,
    canResend: resendSecondsLeft === 0 && step === 'otp',
    sendOtp,
    resendOtp,
    verifyOtp,
    reset,
  };
}
